/**
 * CHART LAB — chapter 2, and the trade a Mercator chart makes.
 *
 * FOUNDER, 2026-08-10: "Charts & Projections: unwrap the sphere into Mercator — this could be superb.
 * Let the student visually see why projection causes distortion."
 *
 * ── THE ONE IDEA ──────────────────────────────────────────────────────────────────────────────
 * A Mercator chart is not a picture of the Earth. It is a BARGAIN, and both halves of it come from
 * the same term:
 *
 *     y = ln(tan(45° + lat/2))
 *
 *   WHAT IT BUYS   a rhumb line — a constant course — is a STRAIGHT line, so a course can be laid
 *                  with a ruler. That is why the chart exists and why it is still used.
 *   WHAT IT COSTS  scale grows as sec(lat), so shapes stay right and SIZES do not. Greenland ends up
 *                  looking like Africa.
 *
 * A candidate who has been told this can recite it. A candidate who has dragged the slider and
 * watched four equal circles become wildly unequal has seen the price with his own eyes, and will
 * not later believe a wall map.
 *
 * ── WHY IT IS THE SAME OBJECT ─────────────────────────────────────────────────────────────────
 * The globe does not become a different component when it flattens; NavSphere blends the two
 * projections. Showing two separate pictures would ask a candidate to believe they are related. This
 * makes him watch one Earth open out, which is the claim itself.
 */

import { useState } from 'react';
import NavSphere from './navsphere.jsx';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';

/* Two legs chosen because the contrast is unmissable at high latitude and nearly invisible near the
   Equator — which is itself the lesson about WHERE the difference matters. */
const LEGS = {
  north: { from: [51.5, -9.5], to: [40.5, -74], label: 'Fastnet → New York' },
  trade: { from: [5, -20], to: [5, 40], label: 'along the Equator' },
};

export default function ChartLab() {
  const [morph, setMorph] = useState(0);
  const [leg, setLeg] = useState('north');
  const [show, setShow] = useState({ tissot: true, route: true, graticule: true, equator: true, primeMeridian: true });

  const flat = morph > 0.5;
  const r = LEGS[leg];

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Chart lab · unwrap the Earth</div>
      <h2 className="g-h2 lab-h">Why a chart cannot tell the whole truth</h2>
      <p className="g-body">
        Drag the slider and watch the globe open into a Mercator chart. The
        <b className="lab-key lab-tissot"> orange circles are all the same size on the Earth</b>.
        Watch what happens to the ones far from the Equator.
      </p>

      <div className="lab-globe">
        <NavSphere height={320} morph={morph} overlays={show}
          route={show.route ? { from: r.from, to: r.to } : null} />
      </div>

      <label className="lab-slider">
        <span className="g-label">Globe → chart</span>
        <input className="lab-range" type="range" min="0" max="1" step="0.01" value={morph}
          onChange={(e) => setMorph(+e.target.value)} aria-label="Unwrap the globe into a chart" />
      </label>

      <div className="shell-picker lab-toggles">
        {[['tissot', 'Equal circles'], ['route', 'The two tracks'],
          ['graticule', 'Graticule'], ['parallels', 'Tropics']].map(([k, l]) => (
          <button key={k} onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))}
            className={`g-btn ${show[k] ? 'g-btn-primary' : ''}`}>{l}</button>
        ))}
      </div>

      <div className="shell-picker lab-toggles">
        {Object.entries(LEGS).map(([k, v]) => (
          <button key={k} onClick={() => setLeg(k)}
            className={`g-btn ${leg === k ? 'g-btn-primary' : ''}`}>{v.label}</button>
        ))}
      </div>

      {/* The reading changes with the slider, because the two states are two different claims and a
          single caption for both would be true of neither. */}
      <div className="lab-task">
        <div className="g-label">{flat ? 'On the chart' : 'On the globe'}</div>
        <div className="lab-result">
          <Value of={computed(flat
            ? 'The dashed rhumb line is now STRAIGHT — that is the whole purpose of this chart, and '
            + 'why a course can be laid on it with a ruler. The blue great circle, which is the '
            + 'SHORTER track, is now a curve. And the equal circles are no longer equal: the far '
            + 'northern one has become enormous.'
            : 'Here the blue great circle is plainly the shorter way round, and the four orange '
            + 'circles are the same size as each other — because on the Earth they are. Now flatten '
            + 'it and watch which of those two facts survives.')} />
        </div>
        <p className="g-body lab-why">
          {flat
            ? 'Shapes are preserved and sizes are not. Every Mercator chart carries this, which is why '
              + 'distance is measured on the LATITUDE scale beside your position — never on the '
              + 'longitude scale along the bottom.'
            : 'Try the equatorial leg as well: near the Equator the two tracks almost coincide, and the '
              + 'distortion is small. The difference is a high-latitude problem.'}
        </p>
      </div>

      <div className="lab-def">
        <Value of={cited(
          'The Mercator chart is a projection in which the meridians are parallel straight lines and '
          + 'the parallels of latitude are straight lines perpendicular to them, the distance between '
          + 'the parallels being increased with the latitude in the same proportion as the meridians '
          + 'are separated, so that a rhumb line is represented by a straight line.',
          'Bowditch, The American Practical Navigator (1888) — public domain')} />
        <p className="g-body lab-why">
          &ldquo;Increased with the latitude in the same proportion&rdquo; is the sentence the slider
          just drew. That single condition is what makes the rhumb line straight and the far north
          enormous — one cause, both consequences.
        </p>
      </div>
    </div>
  );
}
